import { useMemo, useState } from 'react';

import { Flex, Spin, Typography, theme } from '@contiple/antd-ds';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  useBasicInfoQuery,
  useDashboardQuery,
} from '@/hooks/apis/dashboard/query';
import { useAppStore } from '@/store';
import BannerCarousel from './components/BannerCarousel';
import CreateOrganizationGuideModal from './components/CreateOrganizationGuideModal';
import FaqList from './components/FaqList';
import FreeTrialEndedAlertModal from './components/FreeTrialEndedAlertModal';
import FreeTrialGuideModal from './components/FreeTrialGuideModal';
import {
  dismissOrgGuideThisSession,
  isDismissedToday,
  isOrgGuideDismissedThisSession,
} from './utils';
import NewsList from './components/NewsList';
import OrganizationTable from './components/OrganizationTable';
import UsageChart from './components/UsageChart';

const { Text } = Typography;

const Dashboard = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { setFirstSelectedMenu } = useAppStore();
  const { data: response, isLoading } = useDashboardQuery();
  const { data: basicInfoResponse, isLoading: isBasicInfoLoading } =
    useBasicInfoQuery();
  const [guideModalDismissed, setGuideModalDismissed] = useState(
    isOrgGuideDismissedThisSession()
  );
  const [freeTrialModalDismissed, setFreeTrialModalDismissed] = useState(false);
  const [freeTrialEndedAlertDismissed, setFreeTrialEndedAlertDismissed] =
    useState(false);

  const dashboardData = useMemo(() => response?.result?.content, [response]);
  const basicInfo = useMemo(
    () => basicInfoResponse?.result?.content,
    [basicInfoResponse]
  );
  const billingStatus = basicInfo?.billingStatus;

  const hasOrganizations = (dashboardData?.organizations?.length ?? 0) > 0;
  const showGuideModal =
    !isLoading && !hasOrganizations && !guideModalDismissed;

  // 무료 체험 종료 사전 안내 모달 노출 조건:
  // - 무료 체험 기간 중 (status === 'FREETRIAL')
  // - 종료일 기준 D-14 이하 (freeTrialInfo.dday <= 14)
  const showFreeTrialModal =
    !isLoading &&
    !isBasicInfoLoading &&
    hasOrganizations &&
    !freeTrialModalDismissed &&
    billingStatus?.status === 'FREETRIAL' &&
    (billingStatus?.freeTrialInfo?.dday ?? Infinity) <= 14 &&
    !isDismissedToday();

  // 무료 체험 종료 안내 모달 노출 조건:
  // - 백엔드에서 무료 체험이 종료되어 서비스가 자동 해지된 경우 FREETRIAL_ENDED로 응답
  const showFreeTrialEndedAlert =
    !isLoading &&
    !isBasicInfoLoading &&
    !freeTrialEndedAlertDismissed &&
    billingStatus?.contipleNoticeType === 'FREETRIAL_ENDED' &&
    billingStatus?.noticeRequired;

  const ticketSummary = useMemo(() => {
    const dailyUsage = dashboardData?.usage?.dailyUsage ?? [];
    if (dailyUsage.length < 2) return null;

    const yesterday = dailyUsage[dailyUsage.length - 1];
    const dayBefore = dailyUsage[dailyUsage.length - 2];
    const diff = yesterday.totalTicketCount - dayBefore.totalTicketCount;

    return {
      count: yesterday.totalTicketCount,
      diff: Math.abs(diff),
      isIncrease: diff >= 0,
    };
  }, [dashboardData]);

  if (isLoading) {
    return (
      <Flex justify="center" align="center">
        <Spin />
      </Flex>
    );
  }

  return (
    <Flex vertical gap={16} style={{ maxWidth: 1192, margin: '0 auto' }}>
      <style>{`.ctpl-modal .ctpl-modal-container { padding: 0; }`}</style>
      <Flex vertical gap={8}>
        <Text style={{ fontSize: 18 }}>
          <Trans
            i18nKey="dashboard.greeting"
            values={{ name: basicInfo?.userInfo?.name ?? 'Contiple' }}
            components={[
              <Text key="name" style={{ fontSize: 18, fontWeight: 700 }} />,
            ]}
          />
        </Text>
        {hasOrganizations && ticketSummary ? (
          <Text type="secondary">
            <Trans
              i18nKey="dashboard.summaryWithData"
              values={{
                count: ticketSummary.count.toLocaleString(),
                diff: ticketSummary.diff.toLocaleString(),
                direction: t(
                  ticketSummary.isIncrease
                    ? 'dashboard.increased'
                    : 'dashboard.decreased'
                ),
              }}
              components={[
                <Text key="count" strong style={{ color: token.colorText }} />,
                <Text
                  key="diff"
                  strong
                  style={{
                    color: ticketSummary.isIncrease
                      ? token.colorLink
                      : token.colorError,
                  }}
                />,
              ]}
            />
          </Text>
        ) : (
          <Text type="secondary">{t('dashboard.summaryEmpty')}</Text>
        )}
      </Flex>

      <Flex gap={16} style={{ minHeight: 380 }}>
        <UsageChart
          dailyUsage={dashboardData?.usage?.dailyUsage ?? []}
          organizations={dashboardData?.organizations ?? []}
        />
        <OrganizationTable organizations={dashboardData?.organizations ?? []} />
      </Flex>

      <Flex gap={16} align="stretch">
        <NewsList data={dashboardData?.news ?? []} />
        <FaqList data={dashboardData?.faqs ?? []} />
        <BannerCarousel banners={dashboardData?.banners ?? []} />
      </Flex>

      <CreateOrganizationGuideModal
        open={showGuideModal}
        onClose={() => {
          dismissOrgGuideThisSession();
          setGuideModalDismissed(true);
        }}
        onCreate={() => {
          dismissOrgGuideThisSession();
          setGuideModalDismissed(true);
          setFirstSelectedMenu('/organization');
          navigate('/organization/index', {
            state: { openCreateModal: true },
          });
        }}
      />

      <FreeTrialGuideModal
        open={showFreeTrialModal}
        endDate={billingStatus?.freeTrialInfo?.endDate ?? ''}
        dday={billingStatus?.freeTrialInfo?.dday ?? 0}
        onClose={() => setFreeTrialModalDismissed(true)}
      />

      <FreeTrialEndedAlertModal
        open={showFreeTrialEndedAlert}
        onClose={() => setFreeTrialEndedAlertDismissed(true)}
      />
    </Flex>
  );
};

export default Dashboard;
